import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetEventAttendanceQueryKey,
  getListRollCallsQueryKey,
  getGetRollCallEntriesQueryKey,
  useGetEvent,
  useGetEventAttendance,
  useGetAttendanceSettings,
  useUpdateAttendance,
  useListRollCalls,
  useCreateRollCall,
  useGetRollCallEntries,
  useUpdateRollCallEntry,
  type AttendanceList,
  type AttendanceRecord,
  type AttendanceSettings,
  type AttendanceUpdate,
  type CheckoutType,
  type RollCallEntry,
  type RollCallEntryList,
  type RollCallEntryUpdate,
  type RollCallStatus
} from '@cdf/api-client-react';
import {
  ArrowLeftIcon,
  CheckIcon,
  ChevronRightIcon,
  ClockIcon,
  LogOutIcon,
  MessageSquareIcon,
  MicIcon,
  MicOffIcon,
  PlusIcon,
  Undo2Icon,
  UserCheckIcon,
  UserXIcon,
  UsersIcon,
  GraduationCapIcon,
  XIcon
} from 'lucide-react';

import { useSession } from '@/components/auth/user-provider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { cn, getErrorMessage } from '@/lib/utils';
import { useSpeechRecognition } from '@/hooks/use-speech-recognition';
import {
  parseVoiceCommand,
  parseConfirmationCommand,
  defaultIntentForMode,
  intentToRollCallStatus,
  type VoiceIntent,
  type VoiceMatch
} from '@/lib/voice-matcher';

type PendingVoiceConfirm = {
  entry: RollCallEntry;
  status: Extract<RollCallStatus, 'present' | 'absent'>;
  label: string;
  expiresAt: number;
};

type RegisterMode = 'roll_call' | 'sign_out';

type PreferencePresentation = {
  label: string;
  helper: string;
  icon: React.ComponentType<{ className?: string }>;
  badgeClassName: string;
  panelClassName: string;
  iconClassName: string;
  confirmationClassName: string;
};

type UndoSnapshot = {
  entryId: string;
  studentLabel: string;
  message: string;
  previous: RollCallEntryUpdate;
  expiresAt: number;
  fromVoice: boolean;
};

const SWIPE_COMMIT_PX = 88;
const SWIPE_MAX_PX = 140;
const UNDO_MS = 8_000;

const DEFAULT_SETTINGS: AttendanceSettings = {
  voiceEnabled: true
};

function getPreferencePresentation(
  preference: CheckoutType | null | undefined
): PreferencePresentation {
  switch (preference) {
    case 'by_self':
      return {
        label: 'Go home independently',
        helper: 'Parent permission received for an independent departure.',
        icon: CheckIcon,
        badgeClassName: 'border-transparent bg-success text-success-foreground',
        panelClassName: 'border-success/25 bg-success/10',
        iconClassName: 'bg-success/15 text-success',
        confirmationClassName:
          'border-transparent bg-success text-success-foreground hover:bg-success/90'
      };
    case 'wait_for_parent':
      return {
        label: 'Wait for parent pickup',
        helper: 'Keep this student with staff until their parent or guardian is present.',
        icon: ClockIcon,
        badgeClassName: 'border-transparent bg-warning text-warning-foreground',
        panelClassName: 'border-warning/30 bg-warning/10',
        iconClassName: 'bg-warning/20 text-warning-foreground',
        confirmationClassName:
          'border-transparent bg-warning text-warning-foreground hover:bg-warning/90'
      };
    case 'back_to_school':
      return {
        label: 'Return to school',
        helper: 'This student is travelling back to school with the supervising team.',
        icon: MessageSquareIcon,
        badgeClassName: 'border-transparent bg-info text-info-foreground',
        panelClassName: 'border-info/25 bg-info/10',
        iconClassName: 'bg-info/15 text-info',
        confirmationClassName:
          'border-transparent bg-info text-info-foreground hover:bg-info/90'
      };
    case 'with_person':
      return {
        label: 'Leave with named person',
        helper: 'This student is approved to leave with a specific authorised person. Confirm the collecting person name below.',
        icon: UsersIcon,
        badgeClassName: 'border-transparent bg-primary text-primary-foreground',
        panelClassName: 'border-primary/25 bg-primary/10',
        iconClassName: 'bg-primary/15 text-primary',
        confirmationClassName:
          'border-transparent bg-primary text-primary-foreground hover:bg-primary/90'
      };
    case 'with_teacher':
      return {
        label: 'Leave with teacher',
        helper: 'This student will travel back with a supervising teacher.',
        icon: GraduationCapIcon,
        badgeClassName: 'border-transparent bg-secondary text-secondary-foreground',
        panelClassName: 'border-secondary-foreground/25 bg-secondary/10',
        iconClassName: 'bg-secondary/15 text-secondary-foreground',
        confirmationClassName:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/90'
      };
    default:
      return {
        label: 'No parent response',
        helper: 'No verified SMS instruction is on file. Confirm the departure plan with staff.',
        icon: MessageSquareIcon,
        badgeClassName: 'border-border bg-muted text-muted-foreground',
        panelClassName: 'border-border bg-muted/65',
        iconClassName: 'bg-muted text-muted-foreground',
        confirmationClassName:
          'border-transparent bg-primary text-primary-foreground hover:bg-primary/90'
      };
  }
}

function studentLabel(record: AttendanceRecord): string {
  const student = record.student;
  if (!student) return 'Student';
  return `${student.firstName} ${student.lastInitial}.`;
}

function entryLabel(entry: RollCallEntry): string {
  const student = entry.student;
  if (!student) return 'Student';
  return `${student.firstName} ${student.lastInitial}.`;
}

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
}

function patchAttendanceCache(
  queryClient: ReturnType<typeof useQueryClient>,
  eventId: string,
  attendanceId: string,
  patch: Partial<AttendanceRecord>
) {
  queryClient.setQueryData<AttendanceList>(getGetEventAttendanceQueryKey(eventId), (current) => {
    if (!current) return current;
    return {
      ...current,
      attendance: current.attendance.map((row) =>
        row.id === attendanceId ? { ...row, ...patch } : row
      )
    };
  });
}

function patchRollCallEntriesCache(
  queryClient: ReturnType<typeof useQueryClient>,
  rollCallId: string,
  entryId: string,
  patch: Partial<RollCallEntry>
) {
  queryClient.setQueryData<RollCallEntryList>(getGetRollCallEntriesQueryKey(rollCallId), (current) => {
    if (!current) return current;
    return {
      ...current,
      entries: current.entries.map((e) =>
        e.id === entryId ? { ...e, ...patch } : e
      )
    };
  });
}

function sortEntriesWithinGroup(a: RollCallEntry, b: RollCallEntry): number {
  const aPending = a.status === 'pending' ? 0 : 1;
  const bPending = b.status === 'pending' ? 0 : 1;
  if (aPending !== bPending) return aPending - bPending;
  const aName = a.student ? `${a.student.firstName} ${a.student.lastInitial}` : '';
  const bName = b.student ? `${b.student.firstName} ${b.student.lastInitial}` : '';
  return aName.localeCompare(bName);
}

function sortAttendanceWithinGroup(a: AttendanceRecord, b: AttendanceRecord): number {
  const aOut = a.checkinStatus === 'signed_out' ? 1 : 0;
  const bOut = b.checkinStatus === 'signed_out' ? 1 : 0;
  if (aOut !== bOut) return aOut - bOut;
  const aName = a.student ? `${a.student.firstName} ${a.student.lastInitial}` : '';
  const bName = b.student ? `${b.student.firstName} ${b.student.lastInitial}` : '';
  return aName.localeCompare(bName);
}

/* -------------------------------- page -------------------------------- */

export function EventRegisterPage() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { session } = useSession();
  const teacherDefault =
    session.status === 'signedIn' && session.user.name.trim()
      ? session.user.name.trim()
      : '';

  const { data: event, isLoading: eventLoading } = useGetEvent(id);
  const { data: attendanceData, isLoading: attendanceLoading } = useGetEventAttendance(id);
  const { data: settingsData } = useGetAttendanceSettings();
  const settings = settingsData ?? DEFAULT_SETTINGS;
  const attendanceMutation = useUpdateAttendance();
  const rollCallEntryMutation = useUpdateRollCallEntry();
  const createRollCallMutation = useCreateRollCall();

  const attendance = attendanceData?.attendance ?? [];

  // Roll calls for this event
  const { data: rollCallsData } = useListRollCalls(id);
  const rollCalls = rollCallsData?.rollCalls ?? [];

  // Selected roll call — default to the latest (highest sortOrder)
  const [selectedRollCallId, setSelectedRollCallId] = React.useState<string | null>(null);

  // Auto-select the latest roll call when list loads or changes
  React.useEffect(() => {
    if (rollCalls.length === 0) return;
    if (!selectedRollCallId || !rollCalls.some((rc) => rc.id === selectedRollCallId)) {
      const latest = [...rollCalls].sort((a, b) => b.sortOrder - a.sortOrder)[0];
      if (latest) setSelectedRollCallId(latest.id);
    }
  }, [rollCalls, selectedRollCallId]);

  // Fetch entries for the selected roll call
  const { data: entriesData, isLoading: entriesLoading } = useGetRollCallEntries(
    selectedRollCallId ?? '',
    { query: { enabled: !!selectedRollCallId, queryKey: getGetRollCallEntriesQueryKey(selectedRollCallId ?? '') } }
  );
  const entries = entriesData?.entries ?? [];

  const [mode, setMode] = React.useState<RegisterMode>('roll_call');
  /** When false, list all students in one flat list (no group headings). */
  const [groupStudents, setGroupStudents] = React.useState(false);
  /** When false (default), hide students already marked present/absent. */
  const [showMarkedStudents, setShowMarkedStudents] = React.useState(false);
  const [signOutTarget, setSignOutTarget] = React.useState<AttendanceRecord | null>(null);
  const [voiceOpenedSheet, setVoiceOpenedSheet] = React.useState(false);
  const voiceOpenedSheetRef = React.useRef(false);
  voiceOpenedSheetRef.current = voiceOpenedSheet;
  const signOutFormRef = React.useRef<{ checkoutType: CheckoutType; signedOutBy: string; authorizedPersonName: string | null }>({ checkoutType: 'by_self', signedOutBy: '', authorizedPersonName: null });
  const [undo, setUndo] = React.useState<UndoSnapshot | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [voiceFeedback, setVoiceFeedback] = React.useState<string | null>(null);
  const [disambiguation, setDisambiguation] = React.useState<VoiceMatch<RollCallEntry>[] | null>(null);
  const [highlightedId, setHighlightedId] = React.useState<string | null>(null);
  const [pendingVoiceConfirm, setPendingVoiceConfirm] = React.useState<PendingVoiceConfirm | null>(null);
  const pendingVoiceConfirmRef = React.useRef<PendingVoiceConfirm | null>(null);
  pendingVoiceConfirmRef.current = pendingVoiceConfirm;
  const voiceConfirmTimerRef = React.useRef<number | null>(null);
  const [newRollCallName, setNewRollCallName] = React.useState('');
  const [showNewRollCall, setShowNewRollCall] = React.useState(false);
  const undoTimerRef = React.useRef<number | null>(null);
  const voiceFeedbackTimerRef = React.useRef<number | null>(null);
  const highlightTimerRef = React.useRef<number | null>(null);

  /* ---- speech recognition ---- */
  const speech = useSpeechRecognition({ lang: 'en-AU' });
  const voiceAvailable = speech.supported && settings.voiceEnabled;
  const [voiceOn, setVoiceOn] = React.useState(false);

  // Refs for use inside effect without re-subscribing
  const entriesRef = React.useRef(entries);
  entriesRef.current = entries;
  const attendanceRef = React.useRef(attendance);
  attendanceRef.current = attendance;
  const modeRef = React.useRef(mode);
  modeRef.current = mode;
  const settingsRef = React.useRef(settings);
  settingsRef.current = settings;
  const signOutTargetRef = React.useRef(signOutTarget);
  signOutTargetRef.current = signOutTarget;
  const selectedRollCallIdRef = React.useRef(selectedRollCallId);
  selectedRollCallIdRef.current = selectedRollCallId;

  // SSE: refresh attendance + roll call entries on any event update
  React.useEffect(() => {
    if (!id) return;
    const es = new EventSource(`/api/events/${id}/live`);
    es.onmessage = (ev) => {
      void queryClient.invalidateQueries({ queryKey: getGetEventAttendanceQueryKey(id) });
      void queryClient.invalidateQueries({ queryKey: getListRollCallsQueryKey(id) });
      // If the payload has a rollCallId, refresh that roll call's entries
      try {
        const payload = JSON.parse(ev.data) as { rollCallId?: string | null };
        if (payload.rollCallId) {
          void queryClient.invalidateQueries({
            queryKey: getGetRollCallEntriesQueryKey(payload.rollCallId)
          });
        } else if (selectedRollCallIdRef.current) {
          // No specific rollCallId — refresh the currently selected one
          void queryClient.invalidateQueries({
            queryKey: getGetRollCallEntriesQueryKey(selectedRollCallIdRef.current)
          });
        }
      } catch {
        // Not JSON or no data — just refresh selected roll call entries
        if (selectedRollCallIdRef.current) {
          void queryClient.invalidateQueries({
            queryKey: getGetRollCallEntriesQueryKey(selectedRollCallIdRef.current)
          });
        }
      }
    };
    es.onerror = () => {
      // Browser will retry; leave stream open unless unmounted.
    };
    return () => es.close();
  }, [id, queryClient]);

  React.useEffect(() => {
    return () => {
      if (undoTimerRef.current !== null) window.clearTimeout(undoTimerRef.current);
      if (voiceFeedbackTimerRef.current !== null) window.clearTimeout(voiceFeedbackTimerRef.current);
      if (highlightTimerRef.current !== null) window.clearTimeout(highlightTimerRef.current);
      if (voiceConfirmTimerRef.current !== null) window.clearTimeout(voiceConfirmTimerRef.current);
    };
  }, []);

  const queueUndo = React.useCallback((snapshot: Omit<UndoSnapshot, 'expiresAt'>) => {
    if (undoTimerRef.current !== null) window.clearTimeout(undoTimerRef.current);
    setUndo({ ...snapshot, expiresAt: Date.now() + UNDO_MS });
    undoTimerRef.current = window.setTimeout(() => {
      setUndo(null);
      undoTimerRef.current = null;
    }, UNDO_MS);
  }, []);

  const showVoiceFeedback = React.useCallback((msg: string) => {
    setVoiceFeedback(msg);
    if (voiceFeedbackTimerRef.current !== null) window.clearTimeout(voiceFeedbackTimerRef.current);
    voiceFeedbackTimerRef.current = window.setTimeout(() => {
      setVoiceFeedback(null);
      voiceFeedbackTimerRef.current = null;
    }, 3500);
  }, []);

  const flashHighlight = React.useCallback((id: string) => {
    setHighlightedId(id);
    if (highlightTimerRef.current !== null) window.clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = window.setTimeout(() => {
      setHighlightedId(null);
      highlightTimerRef.current = null;
    }, 4000);
  }, []);

  const VOICE_CONFIRM_TIMEOUT_MS = 10_000;

  const clearPendingVoiceConfirm = React.useCallback(() => {
    if (voiceConfirmTimerRef.current !== null) {
      window.clearTimeout(voiceConfirmTimerRef.current);
      voiceConfirmTimerRef.current = null;
    }
    setPendingVoiceConfirm(null);
    pendingVoiceConfirmRef.current = null;
  }, []);

  const setPendingVoiceConfirmWithTimer = React.useCallback((pending: PendingVoiceConfirm) => {
    if (voiceConfirmTimerRef.current !== null) window.clearTimeout(voiceConfirmTimerRef.current);
    setPendingVoiceConfirm(pending);
    pendingVoiceConfirmRef.current = pending;
    voiceConfirmTimerRef.current = window.setTimeout(() => {
      setPendingVoiceConfirm(null);
      pendingVoiceConfirmRef.current = null;
      voiceConfirmTimerRef.current = null;
      showVoiceFeedback('Confirmation timed out');
    }, VOICE_CONFIRM_TIMEOUT_MS);
  }, [showVoiceFeedback]);

  /* ---- roll-call entry marking ---- */
  const markRollCallEntry = React.useCallback(
    async (
      entry: RollCallEntry,
      status: Extract<RollCallStatus, 'present' | 'absent'>,
      fromVoice = false
    ) => {
      if (!selectedRollCallIdRef.current) return;
      if (entry.status === status) return;
      const rollCallId = selectedRollCallIdRef.current;
      const markedBy = teacherDefault || 'Teacher';
      const previousStatus = entry.status;
      const previousMarkedBy = entry.markedBy ?? undefined;
      setError(null);

      const previousRow: RollCallEntry = { ...entry };
      patchRollCallEntriesCache(queryClient, rollCallId, entry.id, {
        status,
        markedBy,
        markedAt: new Date().toISOString()
      });

      try {
        const updated = await rollCallEntryMutation.mutateAsync({
          id: entry.id,
          data: { status, markedBy }
        });
        patchRollCallEntriesCache(queryClient, rollCallId, entry.id, updated);
        // Also invalidate attendance so legacy mirroring is reflected
        void queryClient.invalidateQueries({ queryKey: getGetEventAttendanceQueryKey(id) });
        queueUndo({
          entryId: entry.id,
          studentLabel: entryLabel(entry),
          message: `Marked ${entryLabel(entry)} ${status}`,
          previous: {
            status: previousStatus,
            ...(previousMarkedBy ? { markedBy: previousMarkedBy } : {})
          },
          fromVoice
        });
      } catch (err) {
        patchRollCallEntriesCache(queryClient, rollCallId, entry.id, previousRow);
        setError(getErrorMessage(err));
      }
    },
    [id, queryClient, queueUndo, rollCallEntryMutation, teacherDefault]
  );

  const handleUndo = React.useCallback(async () => {
    if (!undo || !selectedRollCallIdRef.current) return;
    const snapshot = undo;
    const rollCallId = selectedRollCallIdRef.current;
    setUndo(null);
    if (undoTimerRef.current !== null) {
      window.clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
    setError(null);
    try {
      const updated = await rollCallEntryMutation.mutateAsync({
        id: snapshot.entryId,
        data: snapshot.previous
      });
      patchRollCallEntriesCache(queryClient, rollCallId, snapshot.entryId, updated);
      void queryClient.invalidateQueries({ queryKey: getGetEventAttendanceQueryKey(id) });
    } catch (err) {
      setError(getErrorMessage(err));
    }
  }, [id, queryClient, rollCallEntryMutation, undo]);

  /* ---- ad-hoc roll call creation ---- */
  const handleCreateRollCall = React.useCallback(
    async (name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      try {
        const created = await createRollCallMutation.mutateAsync({
          id,
          data: { name: trimmed, createdBy: teacherDefault || 'Teacher' }
        });
        await queryClient.invalidateQueries({ queryKey: getListRollCallsQueryKey(id) });
        setSelectedRollCallId(created.id);
        setShowNewRollCall(false);
        setNewRollCallName('');
      } catch (err) {
        setError(getErrorMessage(err));
      }
    },
    [createRollCallMutation, id, queryClient, teacherDefault]
  );

  const suggestedRollCallName = React.useMemo(() => {
    return `Roll call ${rollCalls.length + 1}`;
  }, [rollCalls.length]);

  /* ---- sign-out (unchanged) ---- */
  const applySignOutUpdate = React.useCallback(
    async (
      record: AttendanceRecord,
      data: AttendanceUpdate,
      options: {
        optimistic: Partial<AttendanceRecord>;
        undoMessage: string;
        previous: AttendanceUpdate;
        fromVoice?: boolean;
      }
    ) => {
      setError(null);
      const previousRow: AttendanceRecord = { ...record };
      patchAttendanceCache(queryClient, id, record.id, {
        ...options.optimistic,
        lastUpdatedAt: new Date().toISOString()
      });

      try {
        const updated = await attendanceMutation.mutateAsync({ id: record.id, data });
        patchAttendanceCache(queryClient, id, record.id, updated);
        void queryClient.invalidateQueries({ queryKey: getGetEventAttendanceQueryKey(id) });
      } catch (err) {
        patchAttendanceCache(queryClient, id, record.id, previousRow);
        setError(getErrorMessage(err));
      }
    },
    [id, attendanceMutation, queryClient]
  );

  /* ---- voice command processing ---- */
  const processVoiceCommand = React.useCallback(
    (transcript: string) => {
      const currentMode = modeRef.current;
      const sheetOpen = signOutTargetRef.current !== null;

      if (currentMode === 'roll_call') {
        // Roll-call mode: match against roll call entries
        const roster = entriesRef.current;

        // ── Two-step confirmation flow ──
        // When a selection is pending, check for confirm/cancel words FIRST,
        // before intent/name parsing. This is critical because "yes"/"yep"
        // overlap with PRESENT_WORDS — we want them treated as confirmation here.
        const pending = pendingVoiceConfirmRef.current;
        if (pending) {
          // Check for undo first — undo keeps existing behavior even during pending
          const undoCheck = parseVoiceCommand<RollCallEntry>(transcript, roster);
          if (undoCheck.intent === 'undo') {
            clearPendingVoiceConfirm();
            void handleUndo();
            showVoiceFeedback('Undoing last mark');
            return;
          }

          // Try name matching FIRST so "Emma here" switches the pending selection
          // to Emma instead of confirming the previously selected student.
          if (undoCheck.ambiguous.length >= 2) {
            setDisambiguation(undoCheck.ambiguous);
            showVoiceFeedback('Multiple matches — tap one');
            return;
          }
          if (undoCheck.match) {
            const match = undoCheck.match;
            const entry = match.record;
            let intent: VoiceIntent = undoCheck.intent;
            if (intent === 'none') intent = defaultIntentForMode(currentMode);
            const targetStatus = intentToRollCallStatus(intent);
            if (targetStatus && entry.status === targetStatus) {
              showVoiceFeedback(`${match.label} already ${targetStatus}`);
              return;
            }
            if (!targetStatus) intent = 'present';
            const status = intent === 'absent' ? 'absent' : 'present';
            flashHighlight(entry.id);
            setPendingVoiceConfirmWithTimer({
              entry,
              status,
              label: match.label,
              expiresAt: Date.now() + VOICE_CONFIRM_TIMEOUT_MS
            });
            showVoiceFeedback(`${match.label} — say 'Here' or 'Ok' to mark ${status}, 'no' to cancel`);
            return;
          }

          // No name matched — now check for a pure confirmation/cancel phrase
          const confirmation = parseConfirmationCommand(transcript);

          if (confirmation === 'confirm') {
            void markRollCallEntry(pending.entry, pending.status, true);
            flashHighlight(pending.entry.id);
            showVoiceFeedback(`Marked ${pending.label} ${pending.status}`);
            clearPendingVoiceConfirm();
            return;
          }

          if (confirmation === 'cancel') {
            clearPendingVoiceConfirm();
            showVoiceFeedback('Cancelled');
            return;
          }

          // Unrecognised transcript while pending — keep the pending selection
          showVoiceFeedback(`Say 'Here' or 'Ok' to confirm, or a name to switch`);
          return;
        }

        // ── No pending selection — normal parsing ──
        const result = parseVoiceCommand<RollCallEntry>(transcript, roster);

        if (result.intent === 'undo') {
          void handleUndo();
          showVoiceFeedback('Undoing last mark');
          return;
        }

        if (result.ambiguous.length >= 2) {
          setDisambiguation(result.ambiguous);
          showVoiceFeedback(`Multiple matches — tap one`);
          return;
        }

        if (!result.match) {
          showVoiceFeedback(`No match for "${result.transcript}"`);
          return;
        }

        const match = result.match;
        const entry = match.record;

        let intent: VoiceIntent = result.intent;
        if (intent === 'none') {
          intent = defaultIntentForMode(currentMode);
        }

        const targetStatus = intentToRollCallStatus(intent);
        if (targetStatus && entry.status === targetStatus) {
          showVoiceFeedback(`${match.label} already ${targetStatus}`);
          return;
        }
        if (!targetStatus) {
          intent = 'present';
        }
        const status = intent === 'absent' ? 'absent' : 'present';

        // Two-step confirm flow — set pending selection, wait for spoken 'Ok' or tap
        flashHighlight(entry.id);
        setPendingVoiceConfirmWithTimer({
          entry,
          status,
          label: match.label,
          expiresAt: Date.now() + VOICE_CONFIRM_TIMEOUT_MS
        });
        showVoiceFeedback(`${match.label} — say 'Here' or 'Ok' to mark ${status}, 'no' to cancel`);
        return;
      }

      // Sign-out mode: match against attendance records
      const roster = attendanceRef.current;

      // If the sign-out sheet is open and was opened by voice, accept spoken
      // confirm/cancel to complete or abort the sign-out.
      if (sheetOpen && voiceOpenedSheetRef.current) {
        const sheetConfirm = parseConfirmationCommand(transcript);
        if (sheetConfirm === 'confirm') {
          const form = signOutFormRef.current;
          if (form.checkoutType === 'with_person' && !form.authorizedPersonName) {
            showVoiceFeedback(`Add the collecting person's name, then tap Confirm`);
            return;
          }
          const record = signOutTargetRef.current;
          if (!record) return;
          setSignOutTarget(null);
          setVoiceOpenedSheet(false);
          void applySignOutUpdate(
            record,
            {
              checkinStatus: 'signed_out',
              checkoutType: form.checkoutType,
              signedOutBy: form.signedOutBy,
              authorizedPersonName: form.checkoutType === 'with_person' ? form.authorizedPersonName : null
            },
            {
              optimistic: {
                checkinStatus: 'signed_out',
                checkoutType: form.checkoutType,
                signedOutBy: form.signedOutBy,
                authorizedPersonName: form.checkoutType === 'with_person' ? form.authorizedPersonName : null
              },
              undoMessage: `Signed out ${studentLabel(record)}`,
              previous: {
                checkinStatus: 'checked_in',
                ...(record.checkoutType ? { checkoutType: record.checkoutType } : {}),
                ...(record.signedOutBy ? { signedOutBy: record.signedOutBy } : {}),
                ...(record.authorizedPersonName ? { authorizedPersonName: record.authorizedPersonName } : {})
              }
            }
          );
          showVoiceFeedback(`Signed out ${studentLabel(record)}`);
          return;
        }
        if (sheetConfirm === 'cancel') {
          setSignOutTarget(null);
          setVoiceOpenedSheet(false);
          showVoiceFeedback('Sign out cancelled');
          return;
        }
        // Unrecognised transcript while sheet is open — keep waiting
        showVoiceFeedback(`Say 'Ok' to confirm sign out, 'no' to cancel`);
        return;
      }

      const result = parseVoiceCommand<AttendanceRecord>(transcript, roster);

      if (result.intent === 'undo') {
        void handleUndo();
        showVoiceFeedback('Undoing last mark');
        return;
      }

      if (result.ambiguous.length >= 2) {
        // For sign-out disambiguation we reuse the same chips but with attendance records
        setDisambiguation(result.ambiguous as unknown as VoiceMatch<RollCallEntry>[]);
        showVoiceFeedback(`Multiple matches — tap one`);
        return;
      }

      if (!result.match) {
        showVoiceFeedback(`No match for "${result.transcript}"`);
        return;
      }

      const match = result.match;
      const record = match.record;

      if (record.checkinStatus === 'signed_out') {
        showVoiceFeedback(`${match.label} already signed out`);
        return;
      }

      if (sheetOpen) {
        showVoiceFeedback(`Finish current sign-out first`);
        return;
      }

      // Voice sign-out always opens the sign-out sheet for confirmation
      setSignOutTarget(record);
      setVoiceOpenedSheet(true);
      flashHighlight(record.id);
      showVoiceFeedback(`${match.label} — say 'Ok' to confirm sign out, 'no' to cancel`);
    },
    [
      applySignOutUpdate,
      clearPendingVoiceConfirm,
      flashHighlight,
      handleUndo,
      markRollCallEntry,
      setPendingVoiceConfirmWithTimer,
      showVoiceFeedback
    ]
  );

  // Watch for final transcripts from the speech hook
  React.useEffect(() => {
    if (!speech.finalTranscript) return;
    const transcript = speech.finalTranscript;
    speech.consumeFinalTranscript();
    processVoiceCommand(transcript);
  }, [speech.finalTranscript, speech.consumeFinalTranscript, processVoiceCommand]);

  // Pause listening when sign-out sheet is open — unless it was opened by voice,
  // in which case we keep listening so the teacher can say "ok" to confirm.
  React.useEffect(() => {
    if (!voiceAvailable) return;
    if (signOutTarget) {
      speech.setEnabled(voiceOpenedSheet);
    } else if (voiceOn) {
      speech.setEnabled(true);
    }
  }, [signOutTarget, voiceOpenedSheet, voiceAvailable, voiceOn, speech]);

  // Turn off voice if the admin disables it while listening
  React.useEffect(() => {
    if (!settings.voiceEnabled && voiceOn) {
      setVoiceOn(false);
      speech.setEnabled(false);
    }
  }, [settings.voiceEnabled, voiceOn, speech]);

  const toggleVoice = React.useCallback(() => {
    if (!voiceAvailable) return;
    const next = !voiceOn;
    setVoiceOn(next);
    speech.setEnabled(next);
    if (!next) {
      setDisambiguation(null);
      clearPendingVoiceConfirm();
    }
  }, [voiceAvailable, voiceOn, speech, clearPendingVoiceConfirm]);

  // Clear pending voice confirmation when mode switches or roll call / event changes
  React.useEffect(() => {
    clearPendingVoiceConfirm();
  }, [mode, selectedRollCallId, id, clearPendingVoiceConfirm]);

  /* ---- roll-call mode: optionally hide already-marked students ---- */
  const visibleEntries = React.useMemo(
    () =>
      showMarkedStudents
        ? entries
        : entries.filter((entry) => entry.status === 'pending'),
    [entries, showMarkedStudents]
  );

  /* ---- roll-call mode: group entries by group ---- */
  const groupedEntries = React.useMemo(() => {
    const map = new Map<string, RollCallEntry[]>();
    for (const entry of visibleEntries) {
      const group = entry.student?.group ?? 'Unassigned';
      if (!map.has(group)) map.set(group, []);
      map.get(group)!.push(entry);
    }
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([group, rows]) => [group, [...rows].sort(sortEntriesWithinGroup)] as const);
  }, [visibleEntries]);

  const flatEntries = React.useMemo(
    () => [...visibleEntries].sort(sortEntriesWithinGroup),
    [visibleEntries]
  );

  /* ---- sign-out mode: group attendance by group ---- */
  const groupedAttendance = React.useMemo(() => {
    const map = new Map<string, AttendanceRecord[]>();
    for (const row of attendance) {
      const group = row.student?.group ?? 'Unassigned';
      if (!map.has(group)) map.set(group, []);
      map.get(group)!.push(row);
    }
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([group, rows]) => [group, [...rows].sort(sortAttendanceWithinGroup)] as const);
  }, [attendance]);

  const flatAttendance = React.useMemo(
    () => [...attendance].sort(sortAttendanceWithinGroup),
    [attendance]
  );

  // Progress stats
  const entryMarked = entries.filter((e) => e.status !== 'pending').length;
  const entryPresent = entries.filter((e) => e.status === 'present').length;
  const entryAbsent = entries.filter((e) => e.status === 'absent').length;
  const stillHere = attendance.filter((a) => a.checkinStatus === 'checked_in').length;
  const signedOut = attendance.filter((a) => a.checkinStatus === 'signed_out').length;

  const progressLabel =
    mode === 'roll_call'
      ? `${entryMarked}/${entries.length} marked`
      : `${signedOut}/${attendance.length} signed out`;

  const isLoading = eventLoading || attendanceLoading || (mode === 'roll_call' && entriesLoading && !!selectedRollCallId);
  const isListening = speech.status === 'listening';
  const isMutating = rollCallEntryMutation.isPending || attendanceMutation.isPending;

  const selectedRollCall = rollCalls.find((rc) => rc.id === selectedRollCallId) ?? null;

  return (
    <div className="-mx-4 -mb-4 flex min-h-[calc(100svh-5.5rem)] flex-col bg-background md:-mx-6 md:-mb-6">
      <header className="sticky top-0 z-30 border-b border-border/80 bg-background/95 px-3 pb-3 pt-2 backdrop-blur supports-backdrop-filter:bg-background/85">
        <div className="flex items-start gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="mt-0.5 size-10 shrink-0"
            asChild
          >
            <Link to={`/events/${id}`} aria-label="Back to event workspace">
              <ArrowLeftIcon className="size-5" />
            </Link>
          </Button>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Mobile register
            </p>
            <h1 className="truncate text-lg font-semibold tracking-tight">
              {event?.name ?? (isLoading ? 'Loading…' : 'Event')}
            </h1>
            <p className="mt-0.5 text-xs text-muted-foreground tabular-nums">{progressLabel}</p>
          </div>

          {/* Mic toggle */}
          {voiceAvailable ? (
            <button
              type="button"
              onClick={toggleVoice}
              aria-label={voiceOn ? 'Stop voice recognition' : 'Start voice recognition'}
              aria-pressed={voiceOn}
              className={cn(
                'mt-0.5 flex shrink-0 items-center gap-2 rounded-xl border px-3 py-2 transition-all',
                voiceOn
                  ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                  : 'border-foreground/30 bg-foreground text-background hover:bg-foreground/90 shadow-md'
              )}
            >
              {voiceOn ? (
                <span className="relative flex items-center justify-center">
                  {isListening && (
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-foreground opacity-30" />
                  )}
                  <MicIcon className="relative size-5" />
                </span>
              ) : (
                <span className="relative flex items-center justify-center" aria-hidden>
                  <MicIcon className="size-5" />
                  {/* Diagonal strike — shows mic is off while keeping the filled button visible */}
                  <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <span className="block h-0.5 w-[1.35rem] rotate-[-45deg] rounded-full bg-background shadow-sm" />
                  </span>
                </span>
              )}
              <span className="text-sm font-semibold">
                {voiceOn ? 'Listening' : 'Voice'}
              </span>
            </button>
          ) : (
            <div
              className="mt-0.5 flex shrink-0 items-center gap-2 rounded-xl border border-dashed border-border/80 bg-muted/40 px-3 py-2 text-muted-foreground"
              title="Voice not supported on this browser"
              aria-label="Voice recognition not available"
            >
              <MicOffIcon className="size-4" />
              <span className="text-sm font-medium">Voice</span>
            </div>
          )}
        </div>

        {/* Voice transcript / feedback strip */}
        {voiceOn && (
          <div className="mt-2 min-h-9 rounded-lg border border-border/80 bg-muted/50 px-3 py-1.5">
            {voiceFeedback ? (
              <p className="text-xs font-medium text-foreground">{voiceFeedback}</p>
            ) : speech.interimTranscript ? (
              <p className="text-xs text-muted-foreground italic">
                {speech.interimTranscript}
              </p>
            ) : speech.errorMessage ? (
              <p className="text-xs text-destructive">{speech.errorMessage}</p>
            ) : isListening ? (
              <p className="text-xs text-muted-foreground">
                Listening… say a name, “here”, “absent”, or “undo”
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">Starting…</p>
            )}
          </div>
        )}

        {/* Unsupported browser notice */}
        {!speech.supported && (
          <div className="mt-2 flex items-center gap-2 rounded-lg border border-border/80 bg-muted/40 px-3 py-1.5">
            <MicOffIcon className="size-3.5 shrink-0 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">
              Voice recognition isn't available on this browser — swipe and tap still work.
            </p>
          </div>
        )}

        {/* Admin disabled voice */}
        {speech.supported && !settings.voiceEnabled && (
          <div className="mt-2 flex items-center gap-2 rounded-lg border border-border/80 bg-muted/40 px-3 py-1.5">
            <MicOffIcon className="size-3.5 shrink-0 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">
              Voice recognition is disabled by an administrator — swipe and tap still work.
            </p>
          </div>
        )}

        {/* Pending voice confirmation (two-step roll-call) */}
        {pendingVoiceConfirm && mode === 'roll_call' && (
          <div className="mt-2 flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-foreground">
                {pendingVoiceConfirm.label}
              </p>
              <p className="text-xs text-muted-foreground">
                Say &lsquo;Here&rsquo; or &lsquo;Ok&rsquo; to mark {pendingVoiceConfirm.status}, or &lsquo;no&rsquo; to cancel
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                void markRollCallEntry(pendingVoiceConfirm.entry, pendingVoiceConfirm.status, true);
                flashHighlight(pendingVoiceConfirm.entry.id);
                showVoiceFeedback(`Marked ${pendingVoiceConfirm.label} ${pendingVoiceConfirm.status}`);
                clearPendingVoiceConfirm();
              }}
              className="flex shrink-0 items-center gap-1 rounded-lg border border-primary bg-primary px-2.5 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
            >
              <CheckIcon className="size-3.5" />
              Confirm
            </button>
            <button
              type="button"
              onClick={() => {
                clearPendingVoiceConfirm();
                showVoiceFeedback('Cancelled');
              }}
              className="flex shrink-0 items-center gap-1 rounded-lg border border-border bg-transparent px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted"
            >
              <XIcon className="size-3.5" />
              Cancel
            </button>
          </div>
        )}

        {/* Disambiguation chips */}
        {disambiguation && disambiguation.length >= 2 && (
          <div className="mt-2">
            <p className="mb-1 text-xs font-medium text-muted-foreground">
              Which student?
            </p>
            <div className="flex flex-wrap gap-1.5">
              {disambiguation.map((m) => (
                <button
                  key={m.record.id}
                  type="button"
                  onClick={() => {
                    const entry = m.record;
                    setDisambiguation(null);
                    if (modeRef.current === 'roll_call') {
                      void markRollCallEntry(entry, 'present', true);
                      flashHighlight(entry.id);
                      showVoiceFeedback(`Marked ${m.label} present`);
                    } else if (modeRef.current === 'sign_out') {
                      // In sign-out mode the disambiguation items are attendance records
                      // cast back from the generic type
                      const record = entry as unknown as AttendanceRecord;
                      if (record.checkinStatus !== 'signed_out') {
                        setSignOutTarget(record);
                        flashHighlight(record.id);
                        showVoiceFeedback(`${m.label} — confirm sign out`);
                      }
                    }
                  }}
                  className="rounded-full border border-border bg-card px-3 py-1.5 text-sm font-medium hover:bg-muted"
                >
                  {m.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setDisambiguation(null)}
                className="rounded-full border border-border bg-transparent px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Mode tabs */}
        <div
          role="tablist"
          aria-label="Register mode"
          className="mt-3 grid grid-cols-2 gap-1 rounded-xl bg-muted p-1"
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'roll_call'}
            className={cn(
              'min-h-11 rounded-lg px-3 text-sm font-semibold transition-colors',
              mode === 'roll_call'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
            onClick={() => setMode('roll_call')}
          >
            Roll call
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'sign_out'}
            className={cn(
              'min-h-11 rounded-lg px-3 text-sm font-semibold transition-colors',
              mode === 'sign_out'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
            onClick={() => setMode('sign_out')}
          >
            Sign out
          </button>
        </div>

        {/* Roll call selector (roll_call mode only) */}
        {mode === 'roll_call' && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2">
              <Select
                value={selectedRollCallId ?? undefined}
                onValueChange={(value) => setSelectedRollCallId(value)}
                disabled={rollCalls.length === 0}
              >
                <SelectTrigger className="min-h-11 flex-1 bg-card" id="roll-call-select">
                  <SelectValue placeholder={
                    rollCalls.length === 0 ? 'No roll calls yet' : 'Select roll call'
                  } />
                </SelectTrigger>
                <SelectContent>
                  {[...rollCalls]
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((rc) => (
                      <SelectItem key={rc.id} value={rc.id}>
                        {rc.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="min-h-11 shrink-0 gap-1.5"
                onClick={() => {
                  setNewRollCallName(suggestedRollCallName);
                  setShowNewRollCall((v) => !v);
                }}
                disabled={createRollCallMutation.isPending}
              >
                <PlusIcon className="size-4" />
                New
              </Button>
            </div>

            {/* Inline new roll call form */}
            {showNewRollCall && (
              <form
                className="flex items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleCreateRollCall(newRollCallName);
                }}
              >
                <Input
                  className="min-h-11 flex-1"
                  placeholder="Roll call name"
                  value={newRollCallName}
                  onChange={(e) => setNewRollCallName(e.target.value)}
                  autoFocus
                  maxLength={200}
                />
                <Button
                  type="submit"
                  size="sm"
                  className="min-h-11 shrink-0"
                  disabled={!newRollCallName.trim() || createRollCallMutation.isPending}
                >
                  Create
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="min-h-11 shrink-0"
                  onClick={() => {
                    setShowNewRollCall(false);
                    setNewRollCallName('');
                  }}
                >
                  Cancel
                </Button>
              </form>
            )}

            {selectedRollCall && (
              <p className="text-xs text-muted-foreground">
                Taking: <span className="font-medium text-foreground">{selectedRollCall.name}</span>
              </p>
            )}
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          {mode === 'roll_call' ? (
            <>
              <Badge className="border-success/20 bg-success/10 text-success">
                <UserCheckIcon className="size-3" />
                {entryPresent} present
              </Badge>
              <Badge className="border-destructive/20 bg-destructive/10 text-destructive">
                <UserXIcon className="size-3" />
                {entryAbsent} absent
              </Badge>
              <Badge className="border-border bg-muted text-muted-foreground">
                {entries.length - entryMarked} pending
              </Badge>
            </>
          ) : (
            <>
              <Badge className="border-warning/30 bg-warning/10 text-warning-foreground">
                <ClockIcon className="size-3" />
                {stillHere} remaining
              </Badge>
              <Badge className="border-info/20 bg-info/10 text-info">
                <LogOutIcon className="size-3" />
                {signedOut} signed out
              </Badge>
            </>
          )}
          <div className="ml-auto flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
            {mode === 'roll_call' && (
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="show-marked-toggle"
                  className="cursor-pointer text-xs font-medium text-muted-foreground"
                >
                  Show marked
                </Label>
                <Switch
                  id="show-marked-toggle"
                  checked={showMarkedStudents}
                  onCheckedChange={setShowMarkedStudents}
                  aria-label="Show students already marked present or absent"
                />
              </div>
            )}
            <div className="flex items-center gap-2">
              <Label
                htmlFor="group-students-toggle"
                className="cursor-pointer text-xs font-medium text-muted-foreground"
              >
                Group by class
              </Label>
              <Switch
                id="group-students-toggle"
                checked={groupStudents}
                onCheckedChange={setGroupStudents}
                aria-label="Group students by class"
              />
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-3 pb-28 pt-3">
        {isLoading && (
          <p className="py-12 text-center text-sm text-muted-foreground">Loading register…</p>
        )}

        {/* Roll call mode: no roll calls */}
        {!isLoading && mode === 'roll_call' && rollCalls.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border px-4 py-12 text-center">
            <p className="font-semibold">No roll calls yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Tap "New" above to create the first roll call for this event.
            </p>
          </div>
        )}

        {/* Roll call mode: no entries */}
        {!isLoading && mode === 'roll_call' && rollCalls.length > 0 && entries.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border px-4 py-12 text-center">
            <p className="font-semibold">No students in this roll call</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Students are added automatically when checked in to the event.
            </p>
          </div>
        )}

        {/* Roll call mode: all visible students filtered out (marked hidden) */}
        {!isLoading &&
          mode === 'roll_call' &&
          rollCalls.length > 0 &&
          entries.length > 0 &&
          visibleEntries.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border px-4 py-12 text-center">
            <p className="font-semibold">Everyone is marked</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Turn on "Show marked" to review present and absent students.
            </p>
          </div>
        )}

        {/* Sign-out mode: no students */}
        {!isLoading && mode === 'sign_out' && attendance.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border px-4 py-12 text-center">
            <p className="font-semibold">No students on this event</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Add students to the event roster from the workspace.
            </p>
          </div>
        )}

        {/* Roll call mode: entry list */}
        {mode === 'roll_call' && groupStudents &&
          groupedEntries.map(([group, rows]) => (
            <section key={group} className="mb-5">
              <div className="mb-2 flex items-baseline justify-between gap-2 px-1">
                <h2 className="text-sm font-semibold tracking-tight">{group}</h2>
                <span className="text-xs text-muted-foreground tabular-nums">{rows.length}</span>
              </div>
              <ul className="space-y-2">
                {rows.map((entry) => (
                  <li key={entry.id}>
                    <SwipeableEntryRow
                      entry={entry}
                      highlighted={highlightedId === entry.id}
                      disabled={isMutating}
                      onMarkPresent={() => void markRollCallEntry(entry, 'present')}
                      onMarkAbsent={() => void markRollCallEntry(entry, 'absent')}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))}

        {mode === 'roll_call' && !groupStudents && flatEntries.length > 0 && (
          <section className="mb-5">
            <div className="mb-2 flex items-baseline justify-between gap-2 px-1">
              <h2 className="text-sm font-semibold tracking-tight">All students</h2>
              <span className="text-xs text-muted-foreground tabular-nums">{flatEntries.length}</span>
            </div>
            <ul className="space-y-2">
              {flatEntries.map((entry) => (
                <li key={entry.id}>
                  <SwipeableEntryRow
                    entry={entry}
                    highlighted={highlightedId === entry.id}
                    disabled={isMutating}
                    onMarkPresent={() => void markRollCallEntry(entry, 'present')}
                    onMarkAbsent={() => void markRollCallEntry(entry, 'absent')}
                  />
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Sign-out mode: attendance list */}
        {mode === 'sign_out' && groupStudents &&
          groupedAttendance.map(([group, rows]) => (
            <section key={group} className="mb-5">
              <div className="mb-2 flex items-baseline justify-between gap-2 px-1">
                <h2 className="text-sm font-semibold tracking-tight">{group}</h2>
                <span className="text-xs text-muted-foreground tabular-nums">{rows.length}</span>
              </div>
              <ul className="space-y-2">
                {rows.map((row) => (
                  <li key={row.id}>
                    <SwipeableSignOutRow
                      record={row}
                      highlighted={highlightedId === row.id}
                      disabled={attendanceMutation.isPending && !signOutTarget}
                      onRequestSignOut={() => setSignOutTarget(row)}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))}

        {mode === 'sign_out' && !groupStudents && flatAttendance.length > 0 && (
          <section className="mb-5">
            <div className="mb-2 flex items-baseline justify-between gap-2 px-1">
              <h2 className="text-sm font-semibold tracking-tight">All students</h2>
              <span className="text-xs text-muted-foreground tabular-nums">{flatAttendance.length}</span>
            </div>
            <ul className="space-y-2">
              {flatAttendance.map((row) => (
                <li key={row.id}>
                  <SwipeableSignOutRow
                    record={row}
                    highlighted={highlightedId === row.id}
                    disabled={attendanceMutation.isPending && !signOutTarget}
                    onRequestSignOut={() => setSignOutTarget(row)}
                  />
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="fixed inset-x-3 bottom-24 z-40 rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive shadow-lg md:inset-x-auto md:left-1/2 md:w-full md:max-w-md md:-translate-x-1/2"
        >
          {error}
        </div>
      )}

      {undo && (
        <div
          role="status"
          className="fixed inset-x-3 bottom-4 z-40 flex items-center gap-3 rounded-2xl border border-border bg-card px-3 py-3 shadow-xl md:inset-x-auto md:left-1/2 md:w-full md:max-w-md md:-translate-x-1/2"
        >
          <p className="min-w-0 flex-1 text-sm font-medium leading-snug">{undo.message}</p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="min-h-10 shrink-0 gap-1.5"
            onClick={() => void handleUndo()}
          >
            <Undo2Icon className="size-3.5" />
            Undo
          </Button>
        </div>
      )}

      <SignOutSheet
        target={signOutTarget}
        defaultTeacherName={teacherDefault}
        busy={attendanceMutation.isPending}
        formRef={signOutFormRef}
        onClose={() => { setSignOutTarget(null); setVoiceOpenedSheet(false); }}
        onConfirm={async ({ checkoutType, signedOutBy, authorizedPersonName }) => {
          if (!signOutTarget) return;
          const record = signOutTarget;
          setSignOutTarget(null);
          setVoiceOpenedSheet(false);
          await applySignOutUpdate(
            record,
            {
              checkinStatus: 'signed_out',
              checkoutType,
              signedOutBy,
              ...(authorizedPersonName !== undefined ? { authorizedPersonName } : {})
            },
            {
              optimistic: {
                checkinStatus: 'signed_out',
                checkoutType,
                signedOutBy,
                ...(authorizedPersonName !== undefined ? { authorizedPersonName } : {})
              },
              undoMessage: `Signed out ${studentLabel(record)}`,
              previous: {
                checkinStatus: 'checked_in',
                ...(record.checkoutType ? { checkoutType: record.checkoutType } : {}),
                ...(record.signedOutBy ? { signedOutBy: record.signedOutBy } : {}),
                ...(record.authorizedPersonName ? { authorizedPersonName: record.authorizedPersonName } : {})
              }
            }
          );
        }}
      />
    </div>
  );
}

/* ----------------------- swipeable roll-call row ----------------------- */

function SwipeableEntryRow({
  entry,
  highlighted,
  disabled,
  onMarkPresent,
  onMarkAbsent
}: {
  entry: RollCallEntry;
  highlighted?: boolean;
  disabled?: boolean;
  onMarkPresent: () => void;
  onMarkAbsent: () => void;
}) {
  const [offsetX, setOffsetX] = React.useState(0);
  const [dragging, setDragging] = React.useState(false);
  const pointerIdRef = React.useRef<number | null>(null);
  const startRef = React.useRef({ x: 0, y: 0 });
  const axisRef = React.useRef<'undecided' | 'horizontal' | 'vertical'>('undecided');
  const offsetRef = React.useRef(0);

  const roll = entry.status;

  const resetOffset = React.useCallback((animate = true) => {
    if (!animate) {
      setDragging(true);
      setOffsetX(0);
      offsetRef.current = 0;
      requestAnimationFrame(() => setDragging(false));
      return;
    }
    setDragging(false);
    setOffsetX(0);
    offsetRef.current = 0;
  }, []);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (event.button !== 0) return;
    pointerIdRef.current = event.pointerId;
    startRef.current = { x: event.clientX, y: event.clientY };
    axisRef.current = 'undecided';
    setDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;
    const dx = event.clientX - startRef.current.x;
    const dy = event.clientY - startRef.current.y;

    if (axisRef.current === 'undecided') {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      axisRef.current = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
      if (axisRef.current === 'vertical') return;
    }
    if (axisRef.current !== 'horizontal') return;

    event.preventDefault();
    const next = Math.max(-SWIPE_MAX_PX, Math.min(SWIPE_MAX_PX, dx));
    offsetRef.current = next;
    setOffsetX(next);
  };

  const finishGesture = (event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;
    pointerIdRef.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // already released
    }

    const dx = offsetRef.current;
    const axis = axisRef.current;
    axisRef.current = 'undecided';
    setDragging(false);

    if (axis !== 'horizontal' || Math.abs(dx) < SWIPE_COMMIT_PX) {
      resetOffset();
      return;
    }

    // Swipe left (negative) = present; swipe right (positive) = absent
    if (dx < 0) {
      resetOffset();
      onMarkPresent();
      return;
    }
    resetOffset();
    onMarkAbsent();
  };

  const revealPresent = Math.max(0, -offsetX) / SWIPE_COMMIT_PX;
  const revealAbsent = Math.max(0, offsetX) / SWIPE_COMMIT_PX;

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl border bg-card shadow-sm transition-shadow',
        highlighted && 'ring-2 ring-primary ring-offset-1 ring-offset-background'
      )}
    >
      {/* Reveal layers — left swipe reveals present; right swipe reveals absent */}
      <div
        aria-hidden
        className="absolute inset-y-0 right-0 flex w-1/2 items-center justify-end gap-2 bg-success/90 px-4 text-success-foreground"
        style={{ opacity: Math.min(1, revealPresent) }}
      >
        <span className="text-sm font-bold uppercase tracking-wide">Present</span>
        <UserCheckIcon className="size-5" />
      </div>
      <div
        aria-hidden
        className="absolute inset-y-0 left-0 flex w-1/2 items-center gap-2 bg-destructive/90 px-4 text-destructive-foreground"
        style={{ opacity: Math.min(1, revealAbsent) }}
      >
        <UserXIcon className="size-5" />
        <span className="text-sm font-bold uppercase tracking-wide">Absent</span>
      </div>

      <div
        role="group"
        aria-label={entryLabel(entry)}
        className={cn(
          'relative touch-pan-y bg-card',
          dragging ? 'cursor-grabbing' : 'cursor-grab'
        )}
        style={{
          transform: `translate3d(${offsetX}px, 0, 0)`,
          transition: dragging ? 'none' : 'transform 160ms ease-out'
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finishGesture}
        onPointerCancel={finishGesture}
      >
        <div
          className={cn(
            'flex min-h-16 items-stretch gap-0',
            roll === 'present' && 'bg-success/5',
            roll === 'absent' && 'bg-destructive/5'
          )}
        >
          <span
            aria-hidden
            className={cn(
              'w-1.5 shrink-0',
              roll === 'pending' && 'bg-muted-foreground/40',
              roll === 'present' && 'bg-success',
              roll === 'absent' && 'bg-destructive'
            )}
          />

          <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 px-3 py-3">
            <div className="flex items-center gap-2">
              <p className="truncate text-base font-semibold tracking-tight">
                {entryLabel(entry)}
              </p>
              {roll === 'present' && (
                <UserCheckIcon className="size-4 shrink-0 text-success" aria-label="Present" />
              )}
              {roll === 'absent' && (
                <UserXIcon className="size-4 shrink-0 text-destructive" aria-label="Absent" />
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {roll === 'pending' && 'Swipe left present · right absent'}
              {roll === 'present' && (
                <>
                  Present
                  {entry.markedAt ? ` · ${formatTime(entry.markedAt)}` : ''}
                  {entry.markedBy ? ` · ${entry.markedBy}` : ''}
                </>
              )}
              {roll === 'absent' && (
                <>
                  Absent
                  {entry.markedAt ? ` · ${formatTime(entry.markedAt)}` : ''}
                  {entry.markedBy ? ` · ${entry.markedBy}` : ''}
                </>
              )}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-1.5 pr-2">
            <Button
              type="button"
              size="sm"
              variant={roll === 'present' ? 'default' : 'outline'}
              className={cn(
                'min-h-11 min-w-11 px-2',
                roll === 'present' &&
                  'border-transparent bg-success text-success-foreground hover:bg-success/90'
              )}
              disabled={disabled}
              aria-label={`Mark ${entryLabel(entry)} present`}
              onClick={(event) => {
                event.stopPropagation();
                onMarkPresent();
              }}
              onPointerDown={(event) => event.stopPropagation()}
            >
              <CheckIcon className="size-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant={roll === 'absent' ? 'default' : 'outline'}
              className={cn(
                'min-h-11 min-w-11 px-2',
                roll === 'absent' &&
                  'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
              disabled={disabled}
              aria-label={`Mark ${entryLabel(entry)} absent`}
              onClick={(event) => {
                event.stopPropagation();
                onMarkAbsent();
              }}
              onPointerDown={(event) => event.stopPropagation()}
            >
              <XIcon className="size-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------- swipeable sign-out row -------------------- */

function SwipeableSignOutRow({
  record,
  highlighted,
  disabled,
  onRequestSignOut
}: {
  record: AttendanceRecord;
  highlighted?: boolean;
  disabled?: boolean;
  onRequestSignOut: () => void;
}) {
  const [offsetX, setOffsetX] = React.useState(0);
  const [dragging, setDragging] = React.useState(false);
  const pointerIdRef = React.useRef<number | null>(null);
  const startRef = React.useRef({ x: 0, y: 0 });
  const axisRef = React.useRef<'undecided' | 'horizontal' | 'vertical'>('undecided');
  const offsetRef = React.useRef(0);

  const signedOut = record.checkinStatus === 'signed_out';

  const resetOffset = React.useCallback((animate = true) => {
    if (!animate) {
      setDragging(true);
      setOffsetX(0);
      offsetRef.current = 0;
      requestAnimationFrame(() => setDragging(false));
      return;
    }
    setDragging(false);
    setOffsetX(0);
    offsetRef.current = 0;
  }, []);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (event.button !== 0) return;
    pointerIdRef.current = event.pointerId;
    startRef.current = { x: event.clientX, y: event.clientY };
    axisRef.current = 'undecided';
    setDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;
    const dx = event.clientX - startRef.current.x;
    const dy = event.clientY - startRef.current.y;

    if (axisRef.current === 'undecided') {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      axisRef.current = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
      if (axisRef.current === 'vertical') return;
    }
    if (axisRef.current !== 'horizontal') return;

    event.preventDefault();
    const next = Math.max(-SWIPE_MAX_PX, Math.min(SWIPE_MAX_PX, dx));
    offsetRef.current = next;
    setOffsetX(next);
  };

  const finishGesture = (event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;
    pointerIdRef.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // already released
    }

    const dx = offsetRef.current;
    const axis = axisRef.current;
    axisRef.current = 'undecided';
    setDragging(false);

    if (axis !== 'horizontal' || Math.abs(dx) < SWIPE_COMMIT_PX) {
      resetOffset();
      return;
    }

    resetOffset();
    if (!signedOut) onRequestSignOut();
  };

  const revealPresent = Math.max(0, offsetX) / SWIPE_COMMIT_PX;
  const revealAbsent = Math.max(0, -offsetX) / SWIPE_COMMIT_PX;

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl border bg-card shadow-sm transition-shadow',
        highlighted && 'ring-2 ring-primary ring-offset-1 ring-offset-background'
      )}
    >
      {/* Reveal layers */}
      <div
        aria-hidden
        className="absolute inset-y-0 right-0 flex w-1/2 items-center justify-end gap-2 bg-info/90 px-4 text-info-foreground"
        style={{ opacity: Math.min(1, Math.max(revealPresent, revealAbsent)) }}
      >
        <span className="text-sm font-bold uppercase tracking-wide">Sign out</span>
        <LogOutIcon className="size-5" />
      </div>
      <div
        aria-hidden
        className="absolute inset-y-0 left-0 flex w-1/2 items-center gap-2 bg-info/90 px-4 text-info-foreground"
        style={{ opacity: Math.min(1, revealPresent) }}
      >
        <LogOutIcon className="size-5" />
        <span className="text-sm font-bold uppercase tracking-wide">Sign out</span>
      </div>

      <div
        role="group"
        aria-label={studentLabel(record)}
        className={cn(
          'relative touch-pan-y bg-card',
          dragging ? 'cursor-grabbing' : 'cursor-grab',
          signedOut && 'opacity-75'
        )}
        style={{
          transform: `translate3d(${offsetX}px, 0, 0)`,
          transition: dragging ? 'none' : 'transform 160ms ease-out'
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finishGesture}
        onPointerCancel={finishGesture}
      >
        <div
          className={cn(
            'flex min-h-16 items-stretch gap-0',
            signedOut && 'bg-info/5'
          )}
        >
          <span
            aria-hidden
            className={cn(
              'w-1.5 shrink-0',
              !signedOut && 'bg-warning',
              signedOut && 'bg-info'
            )}
          />

          <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 px-3 py-3">
            <div className="flex items-center gap-2">
              <p className="truncate text-base font-semibold tracking-tight">
                {studentLabel(record)}
              </p>
              {signedOut && (
                <LogOutIcon className="size-4 shrink-0 text-info" aria-label="Signed out" />
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {!signedOut && (
                <>
                  {getPreferencePresentation(record.smsPreference).label}
                  {record.authorizedPersonName ? `: ${record.authorizedPersonName}` : ''}
                  {' · swipe or tap to sign out'}
                </>
              )}
              {signedOut && (
                <>
                  Signed out
                  {record.authorizedPersonName ? ` · with ${record.authorizedPersonName}` : ''}
                  {record.signedOutBy ? ` · ${record.signedOutBy}` : ''}
                  {` · ${formatTime(record.lastUpdatedAt)}`}
                </>
              )}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-1.5 pr-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="min-h-11 gap-1 px-3"
              disabled={disabled || signedOut}
              aria-label={`Sign out ${studentLabel(record)}`}
              onClick={(event) => {
                event.stopPropagation();
                if (!signedOut) onRequestSignOut();
              }}
              onPointerDown={(event) => event.stopPropagation()}
            >
              {signedOut ? (
                'Done'
              ) : (
                <>
                  Sign out
                  <ChevronRightIcon className="size-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------- sign-out sheet ---------------------------- */

function SignOutSheet({
  target,
  defaultTeacherName,
  busy,
  formRef,
  onClose,
  onConfirm
}: {
  target: AttendanceRecord | null;
  defaultTeacherName: string;
  busy?: boolean;
  formRef: React.MutableRefObject<{ checkoutType: CheckoutType; signedOutBy: string; authorizedPersonName: string | null }>;
  onClose: () => void;
  onConfirm: (input: { checkoutType: CheckoutType; signedOutBy: string; authorizedPersonName?: string | null }) => Promise<void>;
}) {
  const [teacherName, setTeacherName] = React.useState(defaultTeacherName);
  const [checkoutType, setCheckoutType] = React.useState<CheckoutType>('by_self');
  const [personName, setPersonName] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!target) return;
    setCheckoutType(target.smsPreference ?? 'by_self');
    setTeacherName(defaultTeacherName);
    setPersonName(target.authorizedPersonName ?? '');
    setSaving(false);
  }, [target, defaultTeacherName]);

  // Keep the parent's form ref in sync so voice confirmation can read current values
  React.useEffect(() => {
    formRef.current = {
      checkoutType,
      signedOutBy: teacherName.trim() || defaultTeacherName || 'Teacher',
      authorizedPersonName: checkoutType === 'with_person' ? (personName.trim() || null) : null
    };
  }, [checkoutType, teacherName, personName, defaultTeacherName, formRef]);

  const parentInstruction = getPreferencePresentation(target?.smsPreference);
  const selectedCheckout = getPreferencePresentation(checkoutType);
  const ParentIcon = parentInstruction.icon;
  const SelectedIcon = selectedCheckout.icon;

  return (
    <Sheet open={!!target} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="bottom"
        className="max-h-[90svh] overflow-y-auto rounded-t-2xl border-t px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4"
      >
        <SheetHeader className="pr-8 text-left">
          <SheetTitle>
            Sign out {target ? studentLabel(target) : ''}
          </SheetTitle>
          <SheetDescription>
            Confirm the departure plan. This is recorded on the audit trail.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 grid gap-4">
          <div className={cn('flex items-start gap-3 rounded-xl border p-4', parentInstruction.panelClassName)}>
            <span className={cn('rounded-lg p-2', parentInstruction.iconClassName)}>
              <ParentIcon aria-hidden className="size-4" />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold">Parent instruction</p>
                <Badge className={cn('gap-1', parentInstruction.badgeClassName)}>
                  <ParentIcon className="size-3" />
                  {parentInstruction.label}
                </Badge>
              </div>
              <p className="mt-1 text-sm leading-5 text-muted-foreground">
                {parentInstruction.helper}
              </p>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="register-checkout-type">Checkout plan</Label>
            <Select
              value={checkoutType}
              onValueChange={(value) => setCheckoutType(value as CheckoutType)}
            >
              <SelectTrigger id="register-checkout-type" className="min-h-11 bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="by_self">Go home independently</SelectItem>
                <SelectItem value="wait_for_parent">Wait for parent pickup</SelectItem>
                <SelectItem value="back_to_school">Return to school</SelectItem>
                <SelectItem value="with_person">Leave with named person</SelectItem>
                <SelectItem value="with_teacher">Leave with teacher</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div
            className={cn(
              'flex items-center gap-3 rounded-xl border px-3 py-2.5',
              selectedCheckout.panelClassName
            )}
          >
            <span className={cn('rounded-lg p-1.5', selectedCheckout.iconClassName)}>
              <SelectedIcon aria-hidden className="size-3.5" />
            </span>
            <p className="text-sm font-medium">
              Will record: {selectedCheckout.label}
            </p>
          </div>

          {checkoutType === 'with_person' && (
            <div className="grid gap-2">
              <Label htmlFor="register-person-name">Collecting person name</Label>
              <Input
                id="register-person-name"
                className="min-h-11"
                placeholder="e.g. Grandma Sue"
                value={personName}
                onChange={(event) => setPersonName(event.target.value)}
              />
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="register-teacher">Teacher name</Label>
            <Input
              id="register-teacher"
              className="min-h-11"
              placeholder="Your name"
              value={teacherName}
              onChange={(event) => setTeacherName(event.target.value)}
              autoComplete="name"
            />
          </div>

          <div className="grid grid-cols-2 gap-2 pb-2">
            <Button
              type="button"
              variant="outline"
              className="min-h-12"
              onClick={onClose}
              disabled={saving || busy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className={cn('min-h-12', selectedCheckout.confirmationClassName)}
              disabled={saving || busy || (checkoutType === 'with_person' && !personName.trim())}
              onClick={() => {
                void (async () => {
                  setSaving(true);
                  try {
                    await onConfirm({
                      checkoutType,
                      signedOutBy: teacherName.trim() || defaultTeacherName || 'Teacher',
                      authorizedPersonName: checkoutType === 'with_person' ? (personName.trim() || null) : null
                    });
                  } finally {
                    setSaving(false);
                  }
                })();
              }}
            >
              <LogOutIcon className="size-4" />
              {saving ? 'Saving…' : 'Confirm sign out'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
